import React from "react";
import { ChangeEvent, FormEvent, Fragment, KeyboardEvent } from "react";

import ExpandListItem from "./ExpandListItem";

import "../button.css";
import "../icomoon.css";
import "./ExpandList.css";

const INTERNAL_OTHER = "__internal_other";

interface IExpandListProps<T> {
    type?: "select" | "count";
    allowOther?: boolean;
    hideBlankOption?: boolean;
    otherPlaceholderText?: string;

    readOnly?: boolean;
    disableAdd?: boolean;

    items?: T[];
    options?: T[];

    id?: string;
    name?: string;

    autoAdd?: boolean;

    itemRender: (item: T) => React.ReactNode;
    itemButtonRender?: (item: T) => React.ReactNode[] | React.ReactNode;
    optionRender?: (item: T) => React.ReactNode;
    optionOuterRender?: (item: T, index: number) => React.ReactNode;

    itemIdField?: string;

    onAdd?: (event: ExpandListEvent<T>) => void;
    onDelete?: (event: ExpandListEvent<T>) => void;

    inputRenderExtra?: (value?: T) => React.ReactChild | undefined;
}

interface IExpandListState<T> {
    value?: T;
    selectValue: string;
    count: number;

    otherValue: string;

    childSelectValue: string;
    childValue?: T;
    showChild: boolean;

    extraData: { [name: string]: any };
}

// tslint:disable:max-classes-per-file
// tslint:disable-next-line:interface-name
export interface ExpandListEvent<T> {
    origin: ExpandList<T>;
    index?: number;
    item?: T;
    count?: number;
    value?: string;
    childItem?: T;
    extraData?: { [name: string]: any }
}

class ExpandList<T> extends React.Component<IExpandListProps<T>, IExpandListState<T>> {
    constructor(props: Readonly<IExpandListProps<T>>) {
        super(props);

        this.state = {
            count: 1,
            selectValue: props.allowOther && props.hideBlankOption && (!props.options || props.options.length === 0) ? INTERNAL_OTHER : "",

            otherValue: "",

            childSelectValue: "",
            showChild: false,

            extraData: {},
        };

        this.selectChanged = this.selectChanged.bind(this);
        this.childSelectChanged = this.childSelectChanged.bind(this);
        this.countChanged = this.countChanged.bind(this);
        this.otherChanged = this.otherChanged.bind(this);
        this.handleAdd = this.handleAdd.bind(this);
        this.handleDelete = this.handleDelete.bind(this);
        this.handleOtherKeypress = this.handleOtherKeypress.bind(this);
        this.handleChangeExtra = this.handleChangeExtra.bind(this);
    }

    public render() {
        const idField = this.props.itemIdField || "";

        return (
            <div>
                <ul className="expand-list-item-list">
                    {this.props.items && this.props.items.length > 0 ? this.props.items.map((a, index) => <ExpandListItem key={(a as any)[idField]} onDelete={this.handleDelete} index={index} readOnly={this.props.readOnly} extraButtons={this.props.itemButtonRender ? this.props.itemButtonRender(a) : undefined}>{(this.props.itemRender || this.defaultItemRender)(a)}</ExpandListItem>) : <span className="text-light">None added</span>}
                </ul>
                {
                    this.props.readOnly === true || this.props.disableAdd === true
                        ? ""
                        : <div className="expand-list-controls print-hide">
                            <div className="expand-list-row-container" onChange={this.handleChangeExtra} onKeyDown={this.handleOtherKeypress}>
                                <div className="expand-list-new-row">
                                    {
                                        this.props.allowOther === true && this.props.hideBlankOption === true && (!this.props.options || this.props.options.length === 0)
                                            ? <Fragment>
                                                <input type="text" className="other-input" value={this.state.otherValue} onChange={this.otherChanged} onKeyPress={this.handleOtherKeypress} placeholder={this.props.otherPlaceholderText} />
                                            </Fragment>
                                            : <Fragment>
                                                <select className={this.state.selectValue === INTERNAL_OTHER ? "manual-input" : ""} value={this.state.selectValue} onChange={this.selectChanged}>
                                                    {!this.props.hideBlankOption
                                                        ? <option value="" />
                                                        : null}
                                                    {this.props.options
                                                        ? this.props.optionOuterRender
                                                            ? this.props.options.map((a, i) => this.props.optionOuterRender!(a, i))
                                                            : this.props.options.map((a, i) => <option key={(a as any)[idField] || i} value={(a as any)[idField]}>{(this.props.optionRender || this.defaultOptionRender)(a)}</option>)
                                                    : null}
                                                    {this.props.allowOther
                                                        ? <option value={INTERNAL_OTHER}>Other</option>
                                                        : null}
                                                </select>
                                                {
                                                    this.state.showChild
                                                        ? <select value={this.state.childSelectValue} onChange={this.childSelectChanged}>
                                                            {!this.props.hideBlankOption ? <option value="" /> : ""}
                                                            {this.state.value && (this.state.value as any).children ? (this.state.value as any).children.map((a: any, i: number) => <option key={(a as any)[idField] || i} value={(a as any)[idField]}>{(this.props.optionRender || this.defaultOptionRender)(a)}</option>) : ""}
                                                        </select>
                                                        : ""
                                                }
                                                {
                                                    this.state.selectValue === INTERNAL_OTHER
                                                        ? <input type="text" className="other-input" value={this.state.otherValue} onChange={this.otherChanged} onKeyPress={this.handleOtherKeypress} autoFocus={true} placeholder={this.props.otherPlaceholderText} />
                                                        : ""
                                                }
                                                {
                                                    this.props.type === "count"
                                                        ? <input type="number" className="count" min={0} value={this.state.count} onChange={this.countChanged} />
                                                        : ""
                                                }
                                            </Fragment>
                                    }
                                </div>
                                {this.props.inputRenderExtra ? this.props.inputRenderExtra(this.state.value) : ""}
                            </div>
                            <button type="button" className="button" onClick={this.handleAdd} disabled={(!this.state.value || !this.state.selectValue || (this.state.showChild && !this.state.childValue)) && !this.state.otherValue}><i className="icon-plus" /></button>
                        </div>
                }
            </div>
        );
    }

    private selectChanged(e: ChangeEvent<HTMLSelectElement>) {
        if (!this.props.options) {
            return; // Shouldn't be possible
        }

        const value = this.props.options[e.currentTarget.selectedIndex - (this.props.hideBlankOption ? 0 : 1)];

        const hasExtra = !!(this.props.inputRenderExtra && this.props.inputRenderExtra(value));

        this.setState({
            selectValue: e.currentTarget.value,
            value,

            childSelectValue: "",
            childValue: undefined,

            showChild: !!value && !!(value as any).children && (value as any).children.length > 0,

            extraData: {},
        }, () => {
            if (this.props.autoAdd && !this.state.showChild && !hasExtra && this.state.selectValue && this.state.selectValue !== INTERNAL_OTHER) {
                this.handleAdd();
            }
        });
    }

    private childSelectChanged(e: ChangeEvent<HTMLSelectElement>) {
        if (!this.props.options) {
            return; // Shouldn't be possible
        }

        const value = (this.state.value as any).children[e.currentTarget.selectedIndex - (this.props.hideBlankOption ? 0 : 1)];

        this.setState({
            childSelectValue: e.currentTarget.value,
            childValue: value
        }, () => {
            if (this.props.autoAdd && this.state.childSelectValue && this.state.childSelectValue !== INTERNAL_OTHER) {
                this.handleAdd();
            }
        });
    }

    private countChanged(e: ChangeEvent<HTMLInputElement>) {
        this.setState({
            count: parseInt(e.currentTarget.value, 10)
        });
    }

    private otherChanged(e: ChangeEvent<HTMLInputElement>) {
        this.setState({
            otherValue: e.currentTarget.value
        });
    }

    private handleOtherKeypress(e: KeyboardEvent<HTMLInputElement | HTMLDivElement>) {
        if (e.key === "Enter") {
            e.preventDefault();
            this.handleAdd();
        }
    }

    private handleChangeExtra(e: FormEvent<HTMLInputElement | HTMLDivElement>) {
        if (!e.target || !(e.target instanceof HTMLInputElement)) {
            return;
        }

        const nameBase = e.target.name;
        const value = e.target.value;

        const nameMatch = nameBase.match(/el:(.+)/);
        if (!nameMatch) {
            return;
        }

        const name = nameMatch[1];

        this.setState(s => {
            s.extraData[name] = value;

            return { extraData: s.extraData };
        });
    }

    private handleAdd() {
        if (this.props.onAdd && (this.state.value || this.state.otherValue)) {
            if (this.state.selectValue === INTERNAL_OTHER) {
                this.props.onAdd({ origin: this, value: this.state.otherValue, count: this.state.count, extraData: this.state.extraData });
            }
            else {
                if (this.state.showChild) {
                    this.props.onAdd({ origin: this, item: this.state.value, childItem: this.state.childValue, count: this.state.count, extraData: this.state.extraData });
                }
                else {
                    this.props.onAdd({ origin: this, item: this.state.value, count: this.state.count, extraData: this.state.extraData });
                }
            }
        }

        // Clear selected item
        this.setState({
            count: 1,
            otherValue: "",
            selectValue: this.props.allowOther && this.props.hideBlankOption && (!this.props.options || this.props.options.length === 0) ? INTERNAL_OTHER : "",
            value: undefined,

            childSelectValue: "",
            childValue: undefined,
            showChild: false,

            extraData: {},
        });
    }

    private handleDelete(index: number) {
        if (this.props.onDelete && this.props.items) {
            this.props.onDelete({ origin: this, item: this.props.items[index], index });
        }
    }

    private defaultItemRender(item: T) {
        return (item as any).toString();
    }

    private defaultOptionRender(item: T) {
        return (item as any).toString();
    }
}

export default ExpandList;

// tslint:disable:max-classes-per-file
